import { useUserConversion } from "./user-utils-func";

// Helper function to compare arrays and detect changes
const compareArrays = (before = [], after = [], key = "id") => {
  const beforeMap = new Map(before.map((item) => [item[key], item]));
  const afterMap = new Map(after.map((item) => [item[key], item]));

  const added = [];
  const removed = [];
  const unchanged = [];
  const modified = [];

  // Check for removed and modified items
  beforeMap.forEach((beforeItem, id) => {
    const afterItem = afterMap.get(id);
    if (!afterItem) {
      removed.push({ ...beforeItem, status: "REMOVED" });
    } else if (JSON.stringify(beforeItem) !== JSON.stringify(afterItem)) {
      modified.push({
        before: beforeItem,
        after: afterItem,
        status: "MODIFIED",
      });
    } else {
      unchanged.push({ ...beforeItem, status: "UNCHANGED" });
    }
  });

  // Check for added items
  afterMap.forEach((afterItem, id) => {
    if (!beforeMap.has(id)) {
      added.push({ ...afterItem, status: "ADDED" });
    }
  });

  return {
    added,
    removed,
    unchanged,
    modified,
    allItems: [
      ...added,
      ...removed,
      ...unchanged,
      ...modified.map((m) => ({ ...m.after, status: "MODIFIED" })),
    ],
    hasChanges: added.length > 0 || removed.length > 0 || modified.length > 0,
  };
};

// Compare basic fields
const compareBasicFields = (before, after) => {
  const basicFields = [
    "firstName",
    "middleName",
    "lastName",
    "empCode",
    "mobileNo",
    "officePhone",
    "officePhoneExt",
    "restrictedUser",
    "designation",
    "teamId",
    "username",
  ];

  const changes = {};

  basicFields.forEach((field) => {
    const beforeValue = before?.[field];
    const afterValue = after?.[field];

    if (beforeValue !== afterValue) {
      changes[field] = {
        before: beforeValue,
        after: afterValue,
        status:
          beforeValue === undefined
            ? "ADDED"
            : afterValue === undefined
            ? "REMOVED"
            : "MODIFIED",
      };
    }
  });

  return {
    changes,
    hasChanges: Object.keys(changes).length > 0,
  };
};

// Compare user access (resources)
const compareUserAccess = (beforeAccess = [], afterAccess = []) => {
  const resourceGroups = [];

  // Create a map of resource types from both before and after
  const allResourceTypes = new Set([
    ...beforeAccess.map((acc) => acc.resourceType),
    ...afterAccess.map((acc) => acc.resourceType),
  ]);

  allResourceTypes.forEach((resourceType) => {
    const beforeResource = beforeAccess.find(
      (acc) => acc.resourceType === resourceType
    );
    const afterResource = afterAccess.find(
      (acc) => acc.resourceType === resourceType
    );

    const beforeItems =
      beforeResource?.resourceData?.map((item) => ({
        id: item.id,
        name: item.accountLabel || `Resource ${item.id}`,
        resourceType,
      })) || [];

    const afterItems =
      afterResource?.resourceData?.map((item) => ({
        id: item.id,
        name: item.accountLabel || `Resource ${item.id}`,
        resourceType,
      })) || [];

    const resourceComparison = compareArrays(beforeItems, afterItems);

    resourceGroups.push({
      resourceType,
      before: beforeItems,
      after: afterItems,
      changes: resourceComparison.allItems,
      hasChanges: resourceComparison.hasChanges,
      addedCount: resourceComparison.added.length,
      removedCount: resourceComparison.removed.length,
      modifiedCount: resourceComparison.modified.length,
    });
  });

  return {
    resourceGroups,
    hasChanges: resourceGroups.some((group) => group.hasChanges),
    totalAdded: resourceGroups.reduce(
      (sum, group) => sum + group.addedCount,
      0
    ),
    totalRemoved: resourceGroups.reduce(
      (sum, group) => sum + group.removedCount,
      0
    ),
  };
};

// Main difference function
export const calculateUserDifferences = (userBefore, userAfter) => {
  const basicInfo = compareBasicFields(userBefore, userAfter);
  const roles = compareArrays(userBefore?.roles || [], userAfter?.roles || []);
  const regions = compareArrays(
    userBefore?.regionIds || [],
    userAfter?.regionIds || []
  );
  const userReportAccess = compareArrays(
    userBefore?.userReportAccess || [],
    userAfter?.userReportAccess || []
  );
  const systemReportAccess = compareArrays(
    userBefore?.systemReportAccess || [],
    userAfter?.systemReportAccess || []
  );
  const userAccess = compareUserAccess(
    userBefore?.userAccess || [],
    userAfter?.userAccess || []
  );

  const isRestrictedUser =
    userAfter?.restrictedUser ?? userBefore?.restrictedUser;

  return {
    basicInfo,
    roles,
    regions,
    userReportAccess,
    systemReportAccess,
    userAccess,
    isRestrictedUser,
    hasAnyChanges:
      basicInfo.hasChanges ||
      roles.hasChanges ||
      regions.hasChanges ||
      userReportAccess.hasChanges ||
      systemReportAccess.hasChanges ||
      userAccess.hasChanges,

    // Summary counts for quick access
    summary: {
      basicInfoChanges: Object.keys(basicInfo.changes).length,
      rolesAdded: roles.added.length,
      rolesRemoved: roles.removed.length,
      regionsAdded: regions.added.length,
      regionsRemoved: regions.removed.length,
      userReportsAdded: userReportAccess.added.length,
      userReportsRemoved: userReportAccess.removed.length,
      systemReportsAdded: systemReportAccess.added.length,
      systemReportsRemoved: systemReportAccess.removed.length,
      resourcesAdded: userAccess.totalAdded,
      resourcesRemoved: userAccess.totalRemoved,
      resourceTypesChanged: userAccess.resourceGroups.filter(
        (g) => g.hasChanges
      ).length,
    },
  };
};

// Hook to use in components
export const useUserDifferences = (workQueueData) => {
  const { userBefore, userAfter } = workQueueData || {};

  const rawUserBefore = userAfter?.restrictedUser
    ? userBefore
    : {
        ...userBefore,
        regionIds: [],
        userAccess: [],
      };
  const rawUserAfter = userAfter?.restrictedUser
    ? userAfter
    : {
        ...userAfter,
        regionIds: [],
        resourceType: [],
        reconAccountIds: [],
      };

  const { structuredUserBefore, structuredUserAfter } = useUserConversion(
    rawUserAfter,
    rawUserBefore
  );

  const differences = calculateUserDifferences(
    structuredUserBefore,
    structuredUserAfter
  );

  return {
    differences,
    structuredUserBefore,
    structuredUserAfter,
  };
};
